/**
 * @file The host inbox's sidebar badge: how many threads need the host.
 *
 * The count is `needs_action` (host-availability-tech-design §8.8): threads
 * that are new and not archived, or that hold a pending booking request,
 * archived or not. An older backend sends only `new`, which `badgeCountOf`
 * falls back to, so the badge never depends on deploy order
 * (host-availability-implementation-plan rule 1.2.2).
 *
 * Two writers keep it fresh without a poller of its own (host-inbox T10):
 *  - `fetchInboxSummary`, which `hooks/useHostInboxBadge.js` runs on host-area
 *    mount, on window focus, and when the bell's polled unread count rises
 *    (every new enquiry and booking change also writes a bell notification);
 *  - `setInboxCounts`, which the inbox and the booking actions call with the
 *    `counts` of every response, so the badge drops when the server confirms
 *    a `mark_read` or a confirm, and never before (no optimistic updates).
 *
 * A summary that was already in flight when a response's counts landed is
 * older than them, so it is dropped (`setSeq`): otherwise the badge could
 * flip back to a stale count depending on which answer arrived last.
 *
 * It also caches one calendar fact for the sidebar and the Inbox
 * (host-availability-ui-design §3.11): `availabilitySaved`, whether the host
 * has confirmed a time zone. `null` until known; `fetchAvailabilitySetup`
 * reads it once a session (`GET host/calendar`, the current month), and the
 * calendar page writes it from every read and after a zone save
 * (`setAvailabilitySaved`), so the "Set up" pill and the get-ready card go the
 * moment the server says the zone is saved.
 *
 * Not persisted, and reset on logout (store/index.jsx `resetSlices`).
 *
 * Plain .js: no JSX.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Lazy-import, as notificationSlice does: store → slice → api → callAzureFunctions → store.
const getInboxApi = () => import('../../api/hostInbox');
const getCalendarApi = () => import('../../api/hostCalendar');

const toCount = (value) => (Number.isFinite(value) && value > 0 ? Math.floor(value) : 0);

/** The badge number from any `counts` or summary: `needs_action`, else `new`. */
export const badgeCountOf = (counts) => toCount(counts?.needs_action ?? counts?.new);

/**
 * `GET host/inbox/summary` → `{ new, needs_action }`. A failure keeps the last
 * count: the badge is a hint, never an error (contract ask 11), so nothing
 * here throws to a caller or shows a toast.
 */
export const fetchInboxSummary = createAsyncThunk('hostInbox/fetchInboxSummary', async (_, { getState }) => {
  const seq = getState().hostInbox?.setSeq ?? 0;
  const { getInboxSummary } = await getInboxApi();
  const summary = await getInboxSummary();
  return { count: badgeCountOf(summary), seq };
});

/**
 * `GET host/calendar` with no range (the host's current month) → `settings.saved`.
 * Once a session: skipped while one is in flight or the answer is known. A
 * failure leaves it unknown (no pill, no card): a nudge is never an error.
 */
export const fetchAvailabilitySetup = createAsyncThunk(
  'hostInbox/fetchAvailabilitySetup',
  async () => {
    const { getHostCalendar } = await getCalendarApi();
    const calendar = await getHostCalendar();
    return Boolean(calendar?.settings?.saved);
  },
  {
    condition: (_, { getState }) => {
      const { availabilitySaved = null, availabilityLoading = false } = getState().hostInbox ?? {};
      return availabilitySaved === null && !availabilityLoading;
    }
  }
);

export const initialState = {
  badgeCount: 0,
  // Bumped by every direct set; a summary started before the last one is stale.
  setSeq: 0,
  // Whether the host has confirmed their calendar's time zone; null until known.
  availabilitySaved: null,
  availabilityLoading: false
};

const hostInboxSlice = createSlice({
  name: 'hostInbox',
  initialState,
  reducers: {
    /** The `counts` of any inbox or booking response. */
    setInboxCounts: (state, action) => {
      state.badgeCount = badgeCountOf(action.payload);
      state.setSeq += 1;
    },
    /** The calendar's own read or a zone save: the server's `settings.saved`. */
    setAvailabilitySaved: (state, action) => {
      state.availabilitySaved = Boolean(action.payload);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchInboxSummary.fulfilled, (state, action) => {
      if (action.payload.seq !== state.setSeq) return;
      state.badgeCount = action.payload.count;
    });
    builder.addCase(fetchAvailabilitySetup.pending, (state) => {
      state.availabilityLoading = true;
    });
    builder.addCase(fetchAvailabilitySetup.fulfilled, (state, action) => {
      state.availabilityLoading = false;
      // The calendar page may have written a newer answer while this was in flight.
      if (state.availabilitySaved === null) state.availabilitySaved = action.payload;
    });
    builder.addCase(fetchAvailabilitySetup.rejected, (state) => {
      state.availabilityLoading = false;
    });
  }
});

export const { setInboxCounts, setAvailabilitySaved } = hostInboxSlice.actions;

export const selectInboxBadge = (state) => state.hostInbox?.badgeCount ?? 0;

/** `true` | `false` once known; `null` before. */
export const selectAvailabilitySaved = (state) => state.hostInbox?.availabilitySaved ?? null;

export default hostInboxSlice.reducer;
